import {
  type CreateReference,
  IdentifierCode,
  PatientStatusCode,
  type RequiredCoding,
} from '@quromedical/fhir-common';
import { MedicalAidPersonSchema } from '@quromedical/healthbridge/client';
import { z } from 'zod';
import { CallStatusSchema, PatientCallSchema } from '../call/schema';
import {
  DisplayReferenceSchema,
  UUIDInputSchema,
  UUIDSchema,
} from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { type PaginatedBundle, PaginatedBundleSchema } from '../domain/schema';
import {
  MedicalAidMembershipSchema,
  PatientMedicalAidMembershipSchema,
} from '../medical-aid/schema';
import {
  type GeneralUpdate,
  GeneralUpdateSchema,
  PartialPersonSchema,
  type PersonContactWithAddressEmergencyAccess,
  PersonContactWithAddressEmergencyAccessSchema,
  PersonSchema,
} from '../person/schema';
import {
  type BaseRelatedPersonGetResponse,
  BaseCreateRelatedPersonSchema as CreateRelatedPersonSchema,
  BasePartialRelatedPersonSchema as PartialRelatedPersonSchema,
  BaseRelatedPersonGetResponseSchema as RelatedPersonGetResponseSchema,
} from '../shared/person-types';

/**
 * @deprecated - please use `Person.IdentifierType` instead
 */
export enum IdType {
  saIdNumber = IdentifierCode.southAfricanIdNumber,
  namibiaIdNumber = IdentifierCode.namibianIdNumber,
  passportNumber = IdentifierCode.passportNumber,
}

export const GeneralPractitionerUpdateRequestSchema = z.object({
  practitioners: z.string().array(),
  organizations: z.string().array(),
  careTeams: z.string().array().optional(),
});

export const PatientGeneralPractitionerUpdateSchema =
  GeneralPractitionerUpdateRequestSchema.extend({
    id: UUIDSchema,
  });

export type GeneralPractitionerUpdateRequest = z.infer<
  typeof GeneralPractitionerUpdateRequestSchema
>;
export type PatientGeneralPractitionerUpdateRequest =
  GeneralPractitionerUpdateRequest;
export const GeneralPractitionerSchema = z.object({
  practitioners: DisplayReferenceSchema.array(),
  organizations: DisplayReferenceSchema.array(),
  careTeams: DisplayReferenceSchema.array().optional(),
});
export type GeneralPractitioners = z.infer<typeof GeneralPractitionerSchema>;

/** @deprecated admissions are in the process of being moved to their own resource */
type ICD10Code = string;

/** @deprecated admissions are in the process of being moved to their own resource */
export interface AdmissionICD10CodeUpdateRequest {
  admissionICD10Codes: ICD10Code[];
}

/** @deprecated admissions are in the process of being moved to their own resource */
export interface AdmissionICD10CodeUpdateResponse {
  admissionICD10Codes: RequiredCoding[];
}

export const CoverageIdentifiersSchema = z.object({
  schemeCode: z.string(),
  dependantCode: z.string(),
  subscriberId: z.string(),
});

export type CoverageIdentifiers = z.infer<typeof CoverageIdentifiersSchema>;

export const PatientRequiredCodingSchema: z.ZodType<RequiredCoding> = z.object({
  code: z.string(),
  system: z.string(),
  display: z.string().optional(),
  userSelected: z.boolean().optional(),
  version: z.string().optional(),
});

export const PatientSummarySchema = PartialPersonSchema.extend({
  status: z.nativeEnum(PatientStatusCode).optional(),
  generalPractitioner: GeneralPractitionerSchema,
  call: PatientCallSchema.optional(),
});

/**
 * A summary of patient details containing only data that can be mapped from a FHIR patient object
 */
export type PatientSummary = z.infer<typeof PatientSummarySchema>;

export const PatientGetResponseSchema = PatientSummarySchema.extend({
  id: UUIDSchema,
});

export const PatientRequestSchema = UUIDInputSchema.extend({
  withCall: z.boolean().optional(),
});

export type PatientResponse = z.infer<typeof PatientGetResponseSchema>;

export const PatientGetAllResponseSchema = PaginatedBundleSchema.extend({
  patients: PatientGetResponseSchema.array(),
});
export interface PatientGetAllResponse extends PaginatedBundle {
  patients: PatientResponse[];
}

export const PatientSearchSchema = PaginationSchema.extend({
  sort: z.string().optional().describe('The field to sort by'),
  active: z.boolean().optional().describe('Whether the patient is active'),
  name: z.string().optional().describe('The name of the patient'),
  identifier: z.string().optional().describe('The ID number of the patient'),
});

export type PatientSearch = z.infer<typeof PatientSearchSchema>;

export const PatientPrivateOnboardRequestSchema = z.object({
  patient: PartialPersonSchema,
  nextOfKin: CreateRelatedPersonSchema,
});

export type PatientPrivateOnboardRequest = z.infer<
  typeof PatientPrivateOnboardRequestSchema
>;

export const PartialPersonWithoutGeneralSchema = PartialPersonSchema.omit({
  general: true,
});

export const PatientMedicalAidOnboardSchema =
  PartialPersonWithoutGeneralSchema.extend({
    nextOfKin: PartialRelatedPersonSchema.partial().optional(),
  });

export const PatientPrivateOnboardSchema = PartialPersonSchema.extend({
  id: z.string().uuid(),
  nextOfKin: RelatedPersonGetResponseSchema.optional(),
});
export type PatientPrivateOnboard = z.infer<typeof PatientPrivateOnboardSchema>;

export const MedicalAidOnboardPatientSchema = z.object({
  /**
   * For now it is assumed that this is the SA ID number, but at a later stage we may need to add an
   * identifier code below
   */
  identifierValue: z.string(),
  contact: PersonContactWithAddressEmergencyAccessSchema,
});

export const PatientMedicalAidUpdateRequestSchema = z.object({
  /**
   * To convert a patient to a medical aid patient all we need is the selected coverage identifiers
   */
  coverage: CoverageIdentifiersSchema,
  id: UUIDSchema,
});
export type PatientMedicalAidUpdateRequest = z.infer<
  typeof PatientMedicalAidUpdateRequestSchema
>;

export interface CreateResourceResult<TResource> {
  resource: TResource;
  reference: CreateReference;
  fullUrl: string;
}

export const PrivateToMedicalAidConversionResponseSchema = z.object({
  patientId: z.string(),
});
export type PrivateToMedicalAidConversionResponse = z.infer<
  typeof PrivateToMedicalAidConversionResponseSchema
>;

export const PatientOnboardResponseSchema = z.object({
  patientId: z.string(),
});

export type PatientOnboardResponse = z.infer<
  typeof PatientOnboardResponseSchema
>;

export type MedicalAidOnboardCoverage = CoverageIdentifiers;

export const PatientMedicalAidOnboardRequestSchema = z.object({
  patient: MedicalAidOnboardPatientSchema,
  nextOfKin: CreateRelatedPersonSchema,
  coverage: CoverageIdentifiersSchema,
});

export const StartPatientCallRequestSchema = z.object({
  patientId: UUIDSchema,
});

export const EndPatientCallRequestSchema = z.object({
  patientId: UUIDSchema,
  timeValue: z.number().int().min(0),
  status: CallStatusSchema,
});

export type PatientMedicalAidOnboardRequest = z.infer<
  typeof PatientMedicalAidOnboardRequestSchema
>;
export type MedicalAidOnboardPatient = z.infer<
  typeof MedicalAidOnboardPatientSchema
>;
export type StartPatientCallRequest = z.infer<
  typeof StartPatientCallRequestSchema
>;
export type EndPatientCallRequest = z.infer<typeof EndPatientCallRequestSchema>;

export interface UpdateContactPatient {
  contact: Partial<PersonContactWithAddressEmergencyAccess>;
}

export const UpdateGeneralPatientSchema = GeneralUpdateSchema;
export type UpdateGeneralPatient = GeneralUpdate;

export const PatientCoverageResponseSchema = z.object({
  coverage: PatientMedicalAidMembershipSchema.partial().optional(),
  mainMember: MedicalAidPersonSchema.pick({ contact: true, general: true })
    .partial()
    .optional(),
});

export type PatientCoverageResponse = z.infer<
  typeof PatientCoverageResponseSchema
>;

export const PatientCreateCoverageResponseSchema = z.object({
  coverage: PatientMedicalAidMembershipSchema.partial(),
  mainMember: MedicalAidPersonSchema.pick({ contact: true, general: true })
    .partial()
    .optional(),
});

export type PatientCreateCoverageResponse = z.infer<
  typeof PatientCreateCoverageResponseSchema
>;

export interface NextOfKinResponse {
  nextOfKin: BaseRelatedPersonGetResponse;
}

export const PatientMedicalAidOnboardUpdateSchema = PersonSchema.extend({
  id: z.string().uuid(),
  nextOfKin: RelatedPersonGetResponseSchema,
  medicalAidMembership: MedicalAidMembershipSchema,
});

export type PatientMedicalAidOnboardUpdate = z.infer<
  typeof PatientMedicalAidOnboardUpdateSchema
>;

export const PatientQuerySearchSchema = z.object({
  id: UUIDSchema,
  search: z.string().describe('The search query to search for'),
});
export type PatientQuerySearch = z.infer<typeof PatientQuerySearchSchema>;

export const PatientQuerySearchResponseSchema = z.object({
  summary: z.string().describe('The AI summary of the search results'),
});
export type PatientQuerySearchResponse = z.infer<
  typeof PatientQuerySearchResponseSchema
>;

export const PatientClinicalSummarySchemaRequestSchema = z.object({
  id: UUIDSchema,
  admission: UUIDSchema,
});
export type PatientClinicalSummarySchemaRequest = z.infer<
  typeof PatientClinicalSummarySchemaRequestSchema
>;
